/**
 * PfandBetriebModal – Content-View für die Pfandflaschen-Imperium-Betriebsverwaltung
 * (NT-PFAND-IMPERIUM-01, Schritt 5; auf View statt Modal umgestellt in NT-PFAND-UI-JOBFENSTER-01).
 * Trotz Dateiname **kein** Modal mehr — rendert direkt in den Jobs-Content-Bereich
 * (`dashboard.ts::renderJobsTab()`), App-Shell (Header/Nav/Sidebar) bleibt stehen. Schnittstelle:
 * `preparePfandBetriebView()` synchron vor dem Öffnen, `renderPfandBetriebViewShell()` als Markup
 * innerhalb der Jobs-Section, `mountPfandBetriebView()` nach dem DOM-Insert (lädt Daten + Listener),
 * `resetPfandBetriebViewState()` beim Verlassen/Unmount.
 */

import { api, ApiError } from '../services/api.js';
import { showToast } from '../utils/toast.js';
import { refreshBalance } from '../utils/refreshBalance.js';

const ROOT_ID = 'pfand-betrieb-root';

interface PfandBetriebRevierView {
  id: string;
  key: string;
  label: string;
  slots: number;
  mult: number;
  outageUntil: string | null;
  employeeIds: string[];
  /** NT-PFAND-REVIERE-01 */
  kind: 'HEIMAT' | 'BANDE' | 'BRENNPUNKT';
  einflussPercent: number | null;
  nextStufe: { minEinfluss: number; plaetze: number } | null;
  kulanzBis: string | null;
  entfallendePlaetze: number;
}

interface PfandBetriebEmployeeView {
  id: string;
  name: string;
  revierId: string | null;
  status: 'ACTIVE' | 'PAUSED' | 'RESERVE';
  hiredAt: string;
  unpaidSinceAt: string | null;
  /** NT-PFAND-AUSRUESTUNG-01: zugewiesener Wagen, nur hier sichtbar. */
  vehicle: { id: string; vehicleKey: string; label: string; bonusPercent: number } | null;
  activeBonusPercent: number;
  ertragProStundeCents: string;
}

interface PfandAusruestungOption {
  id: string;
  vehicleKey: string;
  label: string;
  bonusPercent: number;
}

interface PfandBetriebCatalogEntry {
  key: string;
  label: string;
  slots: number;
  mult: number;
  costCents: string;
  owned: boolean;
}

interface PfandBetriebOverview {
  kasseCents: string;
  ertragProStundeCents: string;
  lohnProStundeCents: string;
  saldoProStundeCents: string;
  reichweiteStunden: number | null;
  reviere: PfandBetriebRevierView[];
  employees: PfandBetriebEmployeeView[];
  nextHireCostCents: string;
  /** Freie Revier-Plätze gesamt — 0 = EINSTELLEN gesperrt (NT-PFAND-LOCK-01). */
  freeSlots: number;
  catalog: PfandBetriebCatalogEntry[];
  /** Freie, zuweisbare Wagen (NT-PFAND-AUSRUESTUNG-01). Job-Fahrzeug ist nie dabei. */
  ausruestungOptions: PfandAusruestungOption[];
}

interface PfandBetriebResponse {
  unlocked: boolean;
  overview: PfandBetriebOverview | null;
}

let overview: PfandBetriebOverview | null = null;
let loading = false;
let busy = false;
let depositAmount = '';
let withdrawAmount = '';

// NOX-Symbol wie im restlichen Dashboard (dashboard.ts: `&#8358;`), Chris-Feedback: "N" war
// kein Währungssymbol — hier durchgängig durch das echte NOX-Zeichen ersetzt.
const NOX = '&#8358;';

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function fmtCents(cents: string | number): string {
  return (Number(cents) / 100).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtNox(cents: string | number): string {
  return `${fmtCents(cents)}&nbsp;${NOX}`;
}

function fmtSigned(cents: string): string {
  const n = Number(cents);
  const sign = n >= 0 ? '+' : '−';
  return `${sign}${fmtNox(Math.abs(n))}`;
}

/** Vor dem Wechsel in die View aufrufen (synchron, vor renderAll()) — sorgt für einen sauberen
 *  "Wird geladen…"-Zustand statt Reste eines vorherigen Aufrufs kurz aufblitzen zu lassen. */
export function preparePfandBetriebView(): void {
  overview = null;
  busy = false;
  depositAmount = '';
  withdrawAmount = '';
  loading = true;
}

/** Beim Verlassen der View (Zurück-Button) bzw. beim Unmount des gesamten Dashboards. */
export function resetPfandBetriebViewState(): void {
  overview = null;
  loading = false;
  busy = false;
  depositAmount = '';
  withdrawAmount = '';
}

/** Synchrones Markup für den Jobs-Content-Bereich — Daten kommen erst danach via mount(). */
export function renderPfandBetriebViewShell(): string {
  return `<div id="${ROOT_ID}">${renderInner()}</div>`;
}

/** Nach dem DOM-Insert aufrufen: lädt die Daten und hängt die Listener an den Root-Container. */
export async function mountPfandBetriebView(): Promise<void> {
  const root = document.getElementById(ROOT_ID);
  if (!root) return;
  await reload();
  loading = false;
  render();
  attachListeners(root);
}

async function reload(): Promise<void> {
  try {
    const res = await api.get<PfandBetriebResponse>('/pfand/betrieb');
    overview = res.overview;
  } catch {
    showToast('Betrieb konnte nicht geladen werden.', 'error');
  }
}

/** hh:mm bis zu einem Zeitpunkt (Kulanz-Anzeige). */
function fmtCountdown(iso: string): string {
  const ms = Math.max(0, new Date(iso).getTime() - Date.now());
  const totalMin = Math.floor(ms / 60_000);
  return `${String(Math.floor(totalMin / 60)).padStart(2, '0')}:${String(totalMin % 60).padStart(2, '0')}`;
}

function renderRevierRow(revier: PfandBetriebRevierView): string {
  const now = Date.now();
  const isOutaged = revier.outageUntil !== null && new Date(revier.outageUntil).getTime() > now;
  const occupied = revier.employeeIds.length;
  const isDynamic = revier.kind !== 'HEIMAT';
  const isMuted = isDynamic && revier.slots === 0 && occupied === 0;

  // NT-PFAND-REVIERE-01: Einfluss, nächste Stufe, Kulanz (nur Bande/Brennpunkt).
  let einflussZeile = '';
  if (isDynamic) {
    const einfluss = `EINFLUSS ${String(revier.einflussPercent ?? 0).replace('.', ',')}&nbsp;%`;
    const stufe = revier.nextStufe
      ? (revier.slots === 0 && revier.nextStufe.plaetze === 1
        ? `0 PLÄTZE — AB ${String(revier.nextStufe.minEinfluss)}&nbsp;% EINFLUSS`
        : `NÄCHSTER PLATZ AB ${String(revier.nextStufe.minEinfluss)}&nbsp;%`)
      : 'HÖCHSTE STUFE';
    einflussZeile = `<span class="pfand-betrieb-revier-einfluss">${einfluss} · ${stufe}</span>`;
  }
  const kulanzZeile = revier.kulanzBis !== null && revier.entfallendePlaetze > 0
    ? `<span class="pfand-betrieb-revier-kulanz">EINFLUSS GESUNKEN — ${String(revier.entfallendePlaetze)} ${revier.entfallendePlaetze === 1 ? 'PLATZ ENTFÄLLT' : 'PLÄTZE ENTFALLEN'} IN ${fmtCountdown(revier.kulanzBis)}</span>`
    : '';

  return `
    <div class="pfand-betrieb-revier-row${isOutaged ? ' pfand-betrieb-revier-row--outage' : ''}${isMuted ? ' pfand-betrieb-revier-row--muted' : ''}">
      <span class="pfand-betrieb-revier-name">${esc(revier.label)} <span class="pfand-betrieb-revier-mult">×${revier.mult.toFixed(1)}</span></span>
      <span class="pfand-betrieb-revier-slots">${String(occupied)}/${String(revier.slots)} BELEGT</span>
      ${isOutaged ? '<span class="pfand-betrieb-revier-outage-badge">AUSGEFALLEN</span>' : ''}
      ${einflussZeile}
      ${kulanzZeile}
    </div>
  `;
}

const REVIER_GROUPS: { kind: PfandBetriebRevierView['kind']; title: string }[] = [
  { kind: 'HEIMAT', title: 'HEIMAT' },
  { kind: 'BANDE', title: 'BANDEN-REVIER' },
  { kind: 'BRENNPUNKT', title: 'BRENNPUNKTE' },
];

function renderCatalogRow(entry: PfandBetriebCatalogEntry): string {
  if (entry.owned) return '';
  return `
    <div class="pfand-betrieb-revier-row">
      <span class="pfand-betrieb-revier-name">${esc(entry.label)} <span class="pfand-betrieb-revier-mult">×${entry.mult.toFixed(1)}</span> (${String(entry.slots)} Slots)</span>
      <button class="btn-pfand-abgeben" type="button" data-pfand-buy-revier="${esc(entry.key)}" ${busy ? 'disabled' : ''}>
        KAUFEN — ${fmtNox(entry.costCents)}
      </button>
    </div>
  `;
}

/** Revier-Optionen: volle Reviere sind nicht wählbar (außer dem eigenen). NT-PFAND-LOCK-01 — kein
 *  „kein Revier" mehr; nur Angestellte ohne Revier (Reserve/Altbestand) sehen einen Platzhalter. */
function renderRevierOptions(emp: PfandBetriebEmployeeView, reviere: PfandBetriebRevierView[]): string {
  const placeholder = emp.revierId === null
    ? `<option value="" selected disabled>${emp.status === 'RESERVE' ? '— Revier wählen —' : '— kein Revier —'}</option>`
    : '';
  const options = reviere.map((r) => {
    const isOwn = emp.revierId === r.id;
    const isFull = r.employeeIds.length >= r.slots;
    const label = `${esc(r.label)}${isFull && !isOwn ? ' (voll)' : ''}`;
    return `<option value="${esc(r.id)}" ${isOwn ? 'selected' : ''} ${isFull && !isOwn ? 'disabled' : ''}>${label}</option>`;
  });
  return placeholder + options.join('');
}

/** Wagen-Auswahl: eigener Wagen + freie Wagen, oder KEIN WAGEN (NT-PFAND-AUSRUESTUNG-01). */
function renderVehicleOptions(emp: PfandBetriebEmployeeView, free: PfandAusruestungOption[]): string {
  const none = `<option value="" ${emp.vehicle === null ? 'selected' : ''}>KEIN WAGEN</option>`;
  const own = emp.vehicle
    ? `<option value="${esc(emp.vehicle.id)}" selected>${esc(emp.vehicle.label)} (+${String(emp.vehicle.bonusPercent)}&nbsp;%)</option>`
    : '';
  const options = free.map((v) => `<option value="${esc(v.id)}">${esc(v.label)} (+${String(v.bonusPercent)}&nbsp;%)</option>`);
  return none + own + options.join('');
}

function renderEmployeeRow(
  emp: PfandBetriebEmployeeView,
  reviere: PfandBetriebRevierView[],
  free: PfandAusruestungOption[],
): string {
  const statusBadge = emp.status === 'ACTIVE'
    ? '<span class="pfand-betrieb-emp-status pfand-betrieb-emp-status--active">AKTIV</span>'
    : emp.status === 'RESERVE'
      ? '<span class="pfand-betrieb-emp-status pfand-betrieb-emp-status--reserve">RESERVE</span>'
      : '<span class="pfand-betrieb-emp-status pfand-betrieb-emp-status--paused">PAUSIERT</span>';

  const options = renderRevierOptions(emp, reviere);

  return `
    <div class="pfand-betrieb-emp-row">
      <span class="pfand-betrieb-emp-name">${esc(emp.name)}</span>
      ${statusBadge}
      <select class="pfand-betrieb-emp-select" data-pfand-assign="${esc(emp.id)}" ${busy ? 'disabled' : ''}>${options}</select>
      ${emp.status === 'RESERVE' ? '' : `
        <select class="pfand-betrieb-emp-select" data-pfand-vehicle="${esc(emp.id)}" ${busy ? 'disabled' : ''}>${renderVehicleOptions(emp, free)}</select>
      `}
      ${emp.status === 'ACTIVE' && emp.ertragProStundeCents !== '0' ? `
        <span class="pfand-betrieb-emp-ertrag">${fmtNox(emp.ertragProStundeCents)}/STD${emp.activeBonusPercent > 0 ? ` <span class="val-green">+${String(emp.activeBonusPercent)}&nbsp;%</span>` : ''}</span>
      ` : ''}
      <button class="pfand-betrieb-emp-fire" type="button" data-pfand-fire="${esc(emp.id)}" ${busy ? 'disabled' : ''}>FEUERN</button>
    </div>
  `;
}

function renderInner(): string {
  if (loading) {
    return '<p class="dash-empty">Betrieb wird geladen…</p>';
  }

  if (!overview) {
    return '<p class="dash-empty">Betrieb noch nicht freigeschaltet.</p>';
  }

  const o = overview;
  const saldoNegative = Number(o.saldoProStundeCents) < 0;
  const reviereOwnedKeys = new Set(o.reviere.map((r) => r.key));
  const catalogToBuy = o.catalog.filter((c) => !reviereOwnedKeys.has(c.key));

  const kasseEuros = (Number(o.kasseCents) / 100).toFixed(2);
  const belegschaft = o.employees.filter((e) => e.status !== 'RESERVE');
  const reserve = o.employees.filter((e) => e.status === 'RESERVE');
  const allesBelegt = o.freeSlots <= 0;

  return `
    <div class="pfand-betrieb-kasse-highlight">
      <span class="pfand-betrieb-kasse-highlight-label">KASSE</span>
      <span class="pfand-betrieb-kasse-highlight-value">${fmtNox(o.kasseCents)}</span>
    </div>

    <div class="pfand-betrieb-uebersicht">
      <div class="pfand-betrieb-uebersicht-row">
        <span>ERTRAG/STD (Börse heute)</span>
        <span class="val-green">${fmtSigned(o.ertragProStundeCents)}</span>
      </div>
      <div class="pfand-betrieb-uebersicht-row">
        <span>LOHN/STD</span>
        <span class="val-red">−${fmtNox(o.lohnProStundeCents)}</span>
      </div>
      <div class="pfand-betrieb-uebersicht-row pfand-betrieb-uebersicht-row--saldo">
        <span>SALDO/STD</span>
        <span class="${saldoNegative ? 'val-red' : 'val-green'}">
          ${fmtSigned(o.saldoProStundeCents)} ${saldoNegative ? '▼ ZUSCHUSS' : '▲ TRÄGT SICH'}
        </span>
      </div>
      ${saldoNegative && o.reichweiteStunden !== null ? `
        <div class="pfand-betrieb-uebersicht-row">
          <span>KASSE REICHT NOCH</span>
          <span class="val-orange">~${String(o.reichweiteStunden)}&nbsp;STD</span>
        </div>
      ` : ''}
    </div>

    <div class="pfand-betrieb-kasse-actions">
      <div class="pfand-betrieb-kasse-row">
        <input type="number" min="1" class="pfand-betrieb-amount-input" id="pfand-betrieb-deposit-input" placeholder="Betrag eingeben" value="${esc(depositAmount)}" />
        <div class="pfand-betrieb-kasse-actions-zone">
          <button class="pfand-betrieb-btn-gold" type="button" id="pfand-betrieb-deposit-btn" ${busy ? 'disabled' : ''}>EINZAHLEN</button>
        </div>
      </div>
      <div class="pfand-betrieb-kasse-row">
        <input type="number" min="1" class="pfand-betrieb-amount-input" id="pfand-betrieb-withdraw-input" placeholder="Betrag eingeben" value="${esc(withdrawAmount)}" />
        <div class="pfand-betrieb-kasse-actions-zone">
          <button class="pfand-betrieb-max-btn" type="button" id="pfand-betrieb-withdraw-max-btn" data-max="${kasseEuros}" ${busy || o.kasseCents === '0' ? 'disabled' : ''}>MAX</button>
          <button class="pfand-betrieb-btn-gold" type="button" id="pfand-betrieb-withdraw-btn" ${busy ? 'disabled' : ''}>ENTNEHMEN</button>
        </div>
      </div>
    </div>

    <h3 class="pfand-betrieb-section-title">REVIERE</h3>
    ${REVIER_GROUPS.map((g) => {
      const rows = o.reviere.filter((r) => r.kind === g.kind);
      const buyRows = g.kind === 'HEIMAT' ? catalogToBuy.map(renderCatalogRow).join('') : '';
      if (rows.length === 0 && buyRows === '') return '';
      return `
        <h4 class="pfand-betrieb-revier-group">${g.title}</h4>
        <div class="pfand-betrieb-revier-list">
          ${rows.map(renderRevierRow).join('')}
          ${buyRows}
        </div>
      `;
    }).join('')}

    <h3 class="pfand-betrieb-section-title">
      ANGESTELLTE (${String(belegschaft.length)})
      <button class="btn-pfand-abgeben" type="button" id="pfand-betrieb-hire-btn" ${busy || allesBelegt ? 'disabled' : ''}>
        ${allesBelegt ? 'ALLE PLÄTZE BELEGT' : `EINSTELLEN — ${fmtNox(o.nextHireCostCents)}`}
      </button>
    </h3>
    <div class="pfand-betrieb-emp-list">
      ${belegschaft.length === 0
        ? '<p class="dash-empty">Noch keine Angestellten.</p>'
        : belegschaft.map((e) => renderEmployeeRow(e, o.reviere, o.ausruestungOptions)).join('')}
    </div>

    ${reserve.length > 0 ? `
      <h3 class="pfand-betrieb-section-title">RESERVE (${String(reserve.length)})</h3>
      <p class="pfand-betrieb-reserve-info">KEIN LOHN · KEIN ERTRAG · RÜCKT AUTOMATISCH NACH</p>
      <div class="pfand-betrieb-emp-list pfand-betrieb-emp-list--reserve">
        ${reserve.map((e) => renderEmployeeRow(e, o.reviere, o.ausruestungOptions)).join('')}
      </div>
    ` : ''}
  `;
}

function render(): void {
  const root = document.getElementById(ROOT_ID);
  if (!root) return;
  root.innerHTML = renderInner();
}

function attachListeners(root: HTMLElement): void {
  root.addEventListener('click', (e) => {
    const target = e.target as Element;

    if (target.closest('#pfand-betrieb-hire-btn') !== null) { void handleHire(); return; }
    if (target.closest('#pfand-betrieb-deposit-btn') !== null) { void handleDeposit(); return; }
    if (target.closest('#pfand-betrieb-withdraw-btn') !== null) { void handleWithdraw(); return; }

    const maxBtn = target.closest<HTMLButtonElement>('#pfand-betrieb-withdraw-max-btn');
    if (maxBtn !== null && !maxBtn.disabled) {
      withdrawAmount = maxBtn.dataset.max ?? '';
      render();
      return;
    }

    const buyBtn = target.closest<HTMLButtonElement>('[data-pfand-buy-revier]');
    if (buyBtn !== null) { void handleBuyRevier(buyBtn.dataset.pfandBuyRevier ?? ''); return; }

    const fireBtn = target.closest<HTMLButtonElement>('[data-pfand-fire]');
    if (fireBtn !== null) { void handleFire(fireBtn.dataset.pfandFire ?? ''); return; }
  });

  root.addEventListener('input', (e) => {
    const target = e.target as HTMLElement;
    if (target.id === 'pfand-betrieb-deposit-input') depositAmount = (target as HTMLInputElement).value;
    if (target.id === 'pfand-betrieb-withdraw-input') withdrawAmount = (target as HTMLInputElement).value;
  });

  root.addEventListener('change', (e) => {
    const target = e.target as HTMLElement;
    const select = target.closest<HTMLSelectElement>('[data-pfand-assign]');
    if (select !== null) {
      const employeeId = select.dataset.pfandAssign ?? '';
      if (select.value === '') return;
      void handleAssign(employeeId, select.value);
      return;
    }
    const vehicleSelect = target.closest<HTMLSelectElement>('[data-pfand-vehicle]');
    if (vehicleSelect !== null) {
      void handleAssignVehicle(vehicleSelect.dataset.pfandVehicle ?? '', vehicleSelect.value === '' ? null : vehicleSelect.value);
    }
  });
}

/**
 * Sperrt alle Bedienelemente OHNE vollen `innerHTML`-Replace (Chris-Feedback 2026-07-30: Klicks
 * ließen die View sichtbar "zucken" — Ursache war ein voller Re-Render sofort beim Klick UND ein
 * zweiter nach Abschluss, Muster wie der bereits behobene Bug in NT-UI-LAYOUT-RERENDER-FIX-01).
 * Nur der abschließende Re-Render (neue Daten) bleibt ein voller Replace — der ist unvermeidbar,
 * da sich Kasse/Reviere/Angestellte tatsächlich geändert haben können.
 */
function setControlsDisabled(disabled: boolean): void {
  const root = document.getElementById(ROOT_ID);
  if (!root) return;
  root.querySelectorAll<HTMLButtonElement | HTMLSelectElement | HTMLInputElement>('button, select, input').forEach((el) => {
    el.disabled = disabled;
  });
}

async function withBusy(action: () => Promise<void>): Promise<void> {
  if (busy) return;
  busy = true;
  setControlsDisabled(true);
  try {
    await action();
    await reload();
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Aktion fehlgeschlagen.', 'error');
  } finally {
    busy = false;
    render();
  }
}

async function handleHire(): Promise<void> {
  await withBusy(async () => {
    await api.post('/pfand/betrieb/hire', {});
    void refreshBalance();
    showToast('Angestellter eingestellt.', 'info');
  });
}

async function handleFire(employeeId: string): Promise<void> {
  if (!employeeId) return;
  await withBusy(async () => {
    await api.post('/pfand/betrieb/fire', { employeeId });
    showToast('Angestellter entlassen.', 'info');
  });
}

async function handleAssign(employeeId: string, revierId: string): Promise<void> {
  if (!employeeId) return;
  await withBusy(async () => {
    await api.post('/pfand/betrieb/assign', { employeeId, revierId });
  });
}

async function handleAssignVehicle(employeeId: string, vehicleId: string | null): Promise<void> {
  if (!employeeId) return;
  await withBusy(async () => {
    await api.post('/pfand/betrieb/vehicle', { employeeId, vehicleId });
  });
}

async function handleBuyRevier(revierKey: string): Promise<void> {
  if (!revierKey) return;
  await withBusy(async () => {
    await api.post('/pfand/betrieb/revier/buy', { revierKey });
    void refreshBalance();
    showToast('Revier erworben.', 'info');
  });
}

async function handleDeposit(): Promise<void> {
  const euros = parseFloat(depositAmount.replace(',', '.'));
  if (!Number.isFinite(euros) || euros <= 0) { showToast('Ungültiger Betrag.', 'error'); return; }
  const amountCents = Math.round(euros * 100);
  await withBusy(async () => {
    await api.post('/pfand/betrieb/kasse/deposit', { amountCents });
    void refreshBalance();
    depositAmount = '';
    showToast('Eingezahlt.', 'info');
  });
}

async function handleWithdraw(): Promise<void> {
  const euros = parseFloat(withdrawAmount.replace(',', '.'));
  if (!Number.isFinite(euros) || euros <= 0) { showToast('Ungültiger Betrag.', 'error'); return; }
  const amountCents = Math.round(euros * 100);
  await withBusy(async () => {
    await api.post('/pfand/betrieb/kasse/withdraw', { amountCents });
    void refreshBalance();
    withdrawAmount = '';
    showToast('Entnommen.', 'info');
  });
}
